import { useGetContactSubjectsQuery } from '@akinon/next/data/client/account';
import { useGetStoresQuery } from '@akinon/next/data/client/address';
import { useMemo } from 'react';

type StoreSubjectEntry = { subjectId: number; storeName: string };

const TR_CHAR_MAP: Record<string, string> = {
  İ: 'i',
  I: 'i',
  ı: 'i',
  Ş: 's',
  ş: 's',
  Ğ: 'g',
  ğ: 'g',
  Ü: 'u',
  ü: 'u',
  Ö: 'o',
  ö: 'o',
  Ç: 'c',
  ç: 'c'
};

export const normalizeName = (value?: string) =>
  (value ?? '')
    .replace(/[İIıŞşĞğÜüÖöÇç]/g, (char) => TR_CHAR_MAP[char])
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();

const STORE_SUBJECT_PATTERN = /^yatsan\b/;


export function useStoreContactSubjects() {
  const { data: contactSubjects, isLoading } = useGetContactSubjectsQuery();
  const { data: stores } = useGetStoresQuery();

  type Subject = { id: string; is_order_needed: boolean; text: string };
  const allSubjects = useMemo(
    () => (contactSubjects ?? []) as Subject[],
    [contactSubjects]
  );

  const { storeSubjectIds, storeToSubject } = useMemo(() => {
    const ids = new Set<number>();
    const map = new Map<number, StoreSubjectEntry>();

    if (!allSubjects.length) {
      return { storeSubjectIds: ids, storeToSubject: map };
    }

    
    for (const subject of allSubjects) {
      if (STORE_SUBJECT_PATTERN.test(normalizeName(subject.text))) {
        ids.add(Number(subject.id));
      }
    }

    for (const store of stores?.results ?? []) {
      const storeName = normalizeName(store.name);
      if (!storeName) continue;

      const matched = allSubjects.find(
        (subject) => normalizeName(subject.text) === storeName
      );

      if (matched) {
        const subjectId = Number(matched.id);
        ids.add(subjectId);
        map.set(store.pk, { subjectId, storeName: store.name });
      }
    }

    return { storeSubjectIds: ids, storeToSubject: map };
  }, [stores, allSubjects]);

  return {
    allSubjects,
    /** Mağaza subjectleri hariç, genel iletişim subjectleri */
    generalSubjects: allSubjects.filter(
      (s) => !storeSubjectIds.has(Number(s.id))
    ),
    storeSubjectIds,
    /** store.pk → { subjectId, storeName } */
    storeToSubject,
    isLoading
  };
}
